/** Canonical inventory movement kinds stored on `InventoryMovement.kind`. */
export const STOCK_MOVEMENT_KINDS = ["PURCHASE", "SALE", "ADJUSTMENT", "RESERVE"] as const;

export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number];

export const STOCK_MOVEMENT_KIND_LABELS: Record<StockMovementKind, string> = {
  PURCHASE: "Buy / Purchase",
  SALE: "Sell",
  ADJUSTMENT: "Adjustment / Reconcile",
  RESERVE: "Reserve",
};

export const STOCK_MOVEMENT_KIND_HINTS: Record<StockMovementKind, string> = {
  PURCHASE: "Stock additions (purchases, intake, buyback returns)",
  SALE: "Stock reduction from sales, pack pulls, and mechanic wins",
  ADJUSTMENT: "Manual corrections, audit fixes, and returns when a mechanic or sale does not complete",
  RESERVE: "Stock allocated to packs, raffles, auctions, or checkout holds",
};

/** Legacy kinds kept in older rows until migration; map to a canonical kind for display. */
const LEGACY_MOVEMENT_KIND_MAP: Record<string, StockMovementKind> = {
  INTAKE: "PURCHASE",
  VAULT_BUYBACK: "PURCHASE",
  RAFFLE_WIN: "SALE",
  AUCTION_WIN: "SALE",
  PACK_PULL: "SALE",
  TRADE: "SALE",
  SHIPMENT: "SALE",
  RETURN: "ADJUSTMENT",
  RELEASE: "ADJUSTMENT",
};

export function normalizeMovementKind(kind: string): StockMovementKind {
  if (STOCK_MOVEMENT_KINDS.includes(kind as StockMovementKind)) {
    return kind as StockMovementKind;
  }
  return LEGACY_MOVEMENT_KIND_MAP[kind] ?? "ADJUSTMENT";
}

export function movementKindLabel(kind: string): string {
  return STOCK_MOVEMENT_KIND_LABELS[normalizeMovementKind(kind)];
}

/** Manual stock changes: additions are purchases, removals are adjustments. */
export function movementKindForManualDelta(delta: number): StockMovementKind {
  return delta > 0 ? "PURCHASE" : "ADJUSTMENT";
}

export type MovementDisplayRow = {
  kind: string;
  delta: number;
  refType?: string | null;
};

/** Resolve label/badge kind for a movement row (includes legacy mis-tagged manual adds). */
export function resolveMovementDisplayKind(row: MovementDisplayRow): StockMovementKind {
  const normalized = normalizeMovementKind(row.kind);
  if (normalized !== "ADJUSTMENT" || row.delta <= 0) return normalized;

  if (
    row.refType === "PRODUCT_EDIT" ||
    row.refType === "INVENTORY_ADJUST" ||
    row.refType === "STOCK_AUDIT" ||
    row.refType == null
  ) {
    return "PURCHASE";
  }

  return normalized;
}

export function movementRowLabel(row: MovementDisplayRow): string {
  return STOCK_MOVEMENT_KIND_LABELS[resolveMovementDisplayKind(row)];
}

export function movementRowBadgeColor(row: MovementDisplayRow): "green" | "red" | "amber" | "blue" | "gray" {
  return movementKindBadgeColor(resolveMovementDisplayKind(row));
}

export function movementKindDetail(kind: string, note: string | null | undefined): string | null {
  const normalized = normalizeMovementKind(kind);
  if (kind === normalized) return note?.trim() || null;
  const legacy = kind.replace(/_/g, " ").toLowerCase();
  if (note?.trim()) return `${legacy} · ${note.trim()}`;
  return legacy;
}

export function movementKindBadgeColor(kind: string): "green" | "red" | "amber" | "blue" | "gray" {
  switch (normalizeMovementKind(kind)) {
    case "PURCHASE":
      return "green";
    case "SALE":
      return "red";
    case "ADJUSTMENT":
      return "amber";
    case "RESERVE":
      return "blue";
    default:
      return "gray";
  }
}

/** On-hand reductions from customer-facing mechanics and checkout. */
export const SALE_MOVEMENT_REF_TYPES = new Set(["ORDER", "PACK", "RAFFLE", "AUCTION"]);

export function isMechanicMovementKind(kind: string, refType: string | null): boolean {
  const normalized = normalizeMovementKind(kind);
  if (normalized === "SALE" || normalized === "RESERVE") return true;
  if (refType === "PACK" || refType === "RAFFLE" || refType === "AUCTION") return true;
  return false;
}
